import { OfficeViewer } from '@components/shared/OfficeViewer';
import { PdfViewer } from '@components/shared/PdfViewer';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useTruncate } from '@hooks/useTruncate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import TableViewIcon from '@mui/icons-material/TableView';
import {
	Alert,
	Avatar,
	Box,
	Button,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import { isOfficeFile, isPdfFile } from '@utils/fileUtils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	completeUpload,
	getPartUrl,
	initiateUpload,
} from 'services/file-manager/file-managerService';
import { useUploadStore } from 'services/file-manager/file-managerStore';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface ExistingFile {
	id: string;
	fileType: string;
	s3_key: string;
	filename: string;
}

interface FileUploaderProps {
	onFileRemove?: (index: number, file: any) => void;
	showRemoveButton?: boolean;
	title?: string;
	description?: string;
	allowedFormats?: string[];
	maxFileSize?: number;
	multiple?: boolean;
	onUploaded?: (status: any) => void;
	showSupportedFormats?: boolean;
	existingFiles?: ExistingFile[];
	onExistingFileRemove?: (id: string) => void;
}

export default function NewFileUploader({
	onFileRemove,
	showRemoveButton = true,
	title = 'File Uploader',
	description = 'Click to select files',
	allowedFormats = [
		'.jpg',
		'.jpeg',
		'.png',
		'.gif',
		'.webp', // Image formats
		'.pdf',
		'.doc',
		'.docx',
		'.xlsx',
		'.xls', // Document formats
		'.kmz',
		'.kml', // Added support for KMZ and KML formats
	],
	maxFileSize = 200 * 1024 * 1024, // 200MB
	onUploaded,
	showSupportedFormats = false,
	existingFiles = [],
	onExistingFileRemove,
	multiple = false,
}: FileUploaderProps) {
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadStatus, setUploadStatus] = useState<any[]>([]);
	const [processedUploads, setProcessedUploads] = useState<Set<string>>(new Set());
	const [successToastShown, setSuccessToastShown] = useState(false);
	const { globalLoading, setGlobalLoading, emptyUploadStatus } = useUploadStore();
	const { truncate } = useTruncate();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbarStore();
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);
	const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);
	const CDN_URL = process.env.REACT_APP_CDN_URL || '';

	// PDF Viewer state
	const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
	const [currentPdfUrl, setCurrentPdfUrl] = useState('');
	const [currentPdfFileName, setCurrentPdfFileName] = useState('');

	// Office Viewer state
	const [officeViewerOpen, setOfficeViewerOpen] = useState(false);
	const [currentOfficeUrl, setCurrentOfficeUrl] = useState('');
	const [currentOfficeFileName, setCurrentOfficeFileName] = useState('');

	useEffect(() => {
		emptyUploadStatus();
	}, [emptyUploadStatus]);

	// S3 multipart upload constants
	const MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB - S3 minimum part size (except for last part)
	const MAX_PARTS = 10000; // S3 maximum parts allowed
	const MAX_RETRIES = 3;

	// For consistency with file-managerHook.ts
	const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

	/**
	 * Calculate optimal chunking strategy based on file size
	 * @param fileSize Total file size in bytes
	 * @returns { chunkSize, numChunks } Optimal chunk size and number of chunks
	 */
	const calculateChunkingStrategy = (fileSize: number) => {
		// For small files (< 10MB), use a single chunk
		if (fileSize < 10 * 1024 * 1024) {
			return {
				chunkSize: fileSize,
				numChunks: 1,
			};
		}

		// For files where we'll need multiple chunks, calculate optimal strategy

		// 1. Start with minimum part size (5MB)
		let chunkSize = MIN_PART_SIZE;

		// 2. Calculate how many chunks we'd have with minimum size
		let numChunks = Math.ceil(fileSize / chunkSize);

		// 3. If we'd have too many chunks (unlikely but possible for huge files),
		//    increase chunk size to stay under MAX_PARTS limit
		if (numChunks > MAX_PARTS) {
			chunkSize = Math.ceil(fileSize / MAX_PARTS);
			numChunks = MAX_PARTS;
		}

		// 4. Special optimizations for different file size ranges

		// For 10-25MB files, use 4 chunks for better performance
		if (fileSize > 10 * 1024 * 1024 && fileSize <= 25 * 1024 * 1024) {
			numChunks = 4;
			chunkSize = Math.ceil(fileSize / numChunks);
		}
		// For 25-100MB files, use 8-16 chunks for balanced performance
		else if (fileSize > 25 * 1024 * 1024 && fileSize <= 100 * 1024 * 1024) {
			// Target ~10 chunks for medium files
			const targetChunks = Math.min(16, Math.max(8, Math.ceil(fileSize / (10 * 1024 * 1024))));
			numChunks = targetChunks;
			chunkSize = Math.ceil(fileSize / numChunks);
		}
		// For large files (>100MB), use larger chunks for efficiency
		else if (fileSize > 100 * 1024 * 1024) {
			// Target larger chunks (15-20MB) for large files for better throughput
			chunkSize = Math.max(MIN_PART_SIZE, Math.min(20 * 1024 * 1024, Math.ceil(fileSize / 50)));
			numChunks = Math.ceil(fileSize / chunkSize);
		}

		// 5. Final safety check - ensure chunk size is at least minimum
		if (chunkSize < MIN_PART_SIZE) {
			chunkSize = MIN_PART_SIZE;
			numChunks = Math.ceil(fileSize / chunkSize);
		}

		return { chunkSize, numChunks };
	};

	const validateFile = (file: File): { isValid: boolean; error: string | null } => {
		if (file.size > maxFileSize) {
			return { isValid: false, error: t('fileSizeExceedsLimit', { maxFileSize: maxFileSize }) };
		}

		if (allowedFormats.length > 0) {
			const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
			if (!allowedFormats.includes(fileExtension)) {
				return { isValid: false, error: t('fileFormatNotSupported') };
			}
		}

		return { isValid: true, error: null };
	};

	const uploadChunk = async (url: string, chunk: Blob, retryCount = 0): Promise<string> => {
		try {
			const response = await fetch(url, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/octet-stream',
					'Content-Length': chunk.size.toString(),
				},
				body: chunk,
			});

			if (!response.ok) {
				console.error(`Chunk upload failed: Status ${response.status} - ${response.statusText}`);
				throw new Error(`Chunk upload failed: ${response.statusText}`);
			}

			const eTag = response.headers.get('ETag');
			if (!eTag) {
				throw new Error('Missing ETag in response headers');
			}

			return eTag.replace(/"/g, '');
		} catch (error: any) {
			console.error(`Chunk upload error (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error);
			if (retryCount < MAX_RETRIES) {
				await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
				return uploadChunk(url, chunk, retryCount + 1);
			}
			throw error;
		}
	};

	const uploadFile = async (file: File, fileIndex: number): Promise<any> => {
		const { isValid, error } = validateFile(file);
		if (!isValid) {
			setUploadStatus((prev) => {
				const newStatus = [...prev];
				newStatus[fileIndex] = {
					fileName: file.name,
					progress: 0,
					isCompleted: false,
					error,
					key: '', // Clear key for validation errors
					uploadId: '', // Clear uploadId for validation errors
					retryCount: 0,
					isPaused: false,
				};
				return newStatus;
			});
			return null;
		}

		try {
			// Check if we already have an existing upload session from a previous attempt
			const existingStatus = uploadStatus[fileIndex];
			let key: string, uploadId: string;

			if (existingStatus?.key && existingStatus?.uploadId) {
				// Reuse existing upload session
				key = existingStatus.key;
				uploadId = existingStatus.uploadId;
			} else {
				// Only initiate new upload if we don't have an existing session
				const {
					content: { key: newKey, uploadId: newUploadId },
				} = await initiateUpload(file.name);
				key = newKey;
				uploadId = newUploadId;
			}

			setUploadStatus((prev) => {
				const newStatus = [...prev];
				newStatus[fileIndex] = {
					fileName: file.name,
					progress: 0,
					isCompleted: false,
					error: null,
					key,
					retryCount: existingStatus?.retryCount || 0,
					isPaused: false,
					uploadId,
				};
				return newStatus;
			});

			// Special case for small files (under 10MB)
			// For very small files, S3 multipart can be problematic, so we upload in a single chunk
			if (file.size < 10 * 1024 * 1024) {
				const chunk = file.slice(0, file.size);
				const { url } = await getPartUrl(file.name, key, uploadId, 1);
				const eTag = await uploadChunk(url, chunk);

				const parts = [{ partNumber: 1, eTag }];

				setUploadStatus((prev) => {
					const newStatus = [...prev];
					newStatus[fileIndex] = {
						...newStatus[fileIndex],
						progress: 100,
						parts,
					};
					return newStatus;
				});

				// Ensure we have at least one part before calling completeUpload
				if (parts.length > 0) {
					try {
						const completeResponse = await completeUpload(key, uploadId, parts);
					} catch (completeError) {
						console.error(`Error in completeUpload:`, completeError);
						throw completeError;
					}
				} else {
					throw new Error('No parts available to complete upload');
				}

				setUploadStatus((prev) => {
					const newStatus = [...prev];
					newStatus[fileIndex] = {
						...newStatus[fileIndex],
						progress: 100,
						isCompleted: true,
					};
					return newStatus;
				});

				return {
					fileName: file.name,
					progress: 100,
					isCompleted: true,
					key,
					error: null,
					retryCount: 0,
					isPaused: false,
					uploadId,
					parts,
				};
			}

			// Calculate optimal chunking strategy based on file size
			const { chunkSize, numChunks } = calculateChunkingStrategy(file.size);

			const parts: { partNumber: number; eTag: string }[] = [];
			let totalUploaded = 0;
			const chunkSizes: number[] = [];

			for (let i = 0; i < numChunks; i++) {
				const start = i * chunkSize;
				const end = i === numChunks - 1 ? file.size : Math.min((i + 1) * chunkSize, file.size);
				const chunk = file.slice(start, end);

				const currentChunkSize = chunk.size;
				chunkSizes.push(currentChunkSize);

				// Safety check: all chunks except last must be at least CHUNK_SIZE
				if (i < numChunks - 1 && currentChunkSize < CHUNK_SIZE) {
					console.error(
						`Chunk size error: Chunk ${i + 1} size (${currentChunkSize} bytes) is less than minimum S3 part size (${CHUNK_SIZE} bytes)`
					);
					throw new Error(
						`Chunk ${i + 1} size (${currentChunkSize} bytes) is less than minimum S3 part size (${CHUNK_SIZE} bytes)`
					);
				}

				const { url } = await getPartUrl(file.name, key, uploadId, i + 1);
				const eTag = await uploadChunk(url, chunk);

				parts.push({ partNumber: i + 1, eTag });
				totalUploaded += currentChunkSize;

				const progress = Math.round((totalUploaded / file.size) * 100);
				setUploadStatus((prev) => {
					const newStatus = [...prev];
					newStatus[fileIndex] = {
						...newStatus[fileIndex],
						progress,
						parts,
					};
					return newStatus;
				});
			}

			// Ensure we have at least one part before calling completeUpload
			if (parts.length > 0) {
				try {
					const completeResponse = await completeUpload(key, uploadId, parts);
				} catch (completeError: any) {
					console.error(`Error in completeUpload: ${completeError.message}`, completeError);
					throw completeError;
				}
			} else {
				throw new Error('No parts available to complete upload');
			}

			setUploadStatus((prev) => {
				const newStatus = [...prev];
				newStatus[fileIndex] = {
					...newStatus[fileIndex],
					progress: 100,
					isCompleted: true,
				};
				return newStatus;
			});

			return {
				fileName: file.name,
				progress: 100,
				isCompleted: true,
				key,
				error: null,
				retryCount: 0,
				isPaused: false,
				uploadId,
				parts,
			};
		} catch (error: any) {
			setUploadStatus((prev) => {
				const newStatus = [...prev];
				const currentRetryCount = newStatus[fileIndex]?.retryCount || 0;
				const existingKey = newStatus[fileIndex]?.key || '';
				const existingUploadId = newStatus[fileIndex]?.uploadId || '';
				
				newStatus[fileIndex] = {
					fileName: file.name,
					progress: 0,
					isCompleted: false,
					key: existingKey, // Preserve existing key for retries
					uploadId: existingUploadId, // Preserve existing uploadId for retries
					error: error.message || 'Upload failed',
					retryCount: currentRetryCount,
					isPaused: false,
				};
				return newStatus;
			});
			return null;
		}
	};

	const uploadFileWithRetry = async (file: File, fileIndex: number, maxRetries: number = 3): Promise<any> => {
		const currentRetryCount = uploadStatus[fileIndex]?.retryCount || 0;
		
		const result = await uploadFile(file, fileIndex);
		
		if (result === null && currentRetryCount < maxRetries) {
			setUploadStatus((prev) => {
				const newStatus = [...prev];
				newStatus[fileIndex] = {
					...newStatus[fileIndex],
					retryCount: currentRetryCount + 1,
				};
				return newStatus;
			});
			
			await new Promise((resolve) => setTimeout(resolve, 1000 * (currentRetryCount + 1)));
			
			return uploadFileWithRetry(file, fileIndex, maxRetries);
		}
		
		// If upload failed after all retries, show error toast
		if (result === null) {
			showSnackbar(
				t('uploadFailed') || 'Upload Failed',
				t('fileUploadFailedAfterRetries', { fileName: file.name, maxRetries }) || 
				`Failed to upload "${file.name}" after ${maxRetries} attempts.`,
				'error'
			);
		}
		
		return result;
	};

	const uploadFiles = async (files: File[]) => {
		if (files.length === 0) return;

		setIsUploading(true);
		setGlobalLoading(true);
		setSuccessToastShown(false); // Reset success toast flag for new upload batch

		try {
			const currentFileCount = uploadStatus.length;

			setSelectedFiles((prev) => [...prev, ...files]);

			const newUploadStatus = files.map((file) => ({
				fileName: file.name,
				progress: 0,
				isCompleted: false,
				error: null,
				key: '',
				retryCount: 0,
				isPaused: false,
			}));

			setUploadStatus((prev) => [...prev, ...newUploadStatus]);

			const uploadPromises: Promise<any>[] = [];

			for (let i = 0; i < files.length; i++) {
				const file = files[i];

				const fileIndex = currentFileCount + i;
				const uploadPromise = uploadFileWithRetry(file, fileIndex);
				uploadPromises.push(uploadPromise);
			}

			await Promise.all(uploadPromises);
		} catch (error) {
			console.error('Error uploading files:', error);
		} finally {
			setIsUploading(false);
			setGlobalLoading(false);
		}
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length > 0) {
			const filesToUpload = multiple ? files : [files[0]];
			await uploadFiles(filesToUpload);

			event.target.value = '';
		}
	};

	const handleRemoveFile = (index: number) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

		setUploadStatus((prev) => prev.filter((_, i) => i !== index));

		if (onFileRemove) {
			onFileRemove(index, uploadStatus[index]);
		}
	};

	const handleExistingFileRemove = (id: string) => {
		if (onExistingFileRemove) {
			onExistingFileRemove(id);
		}
	};

	const handleFileClick = (s3Key: string, fileName: string, fileType?: string) => {
		// Check if s3Key already contains CDN_URL to prevent duplication
		const fileUrl = s3Key.startsWith(CDN_URL) ? s3Key : `${CDN_URL}${s3Key}`;

		if (isPdfFile(fileName)) {
			setCurrentPdfUrl(fileUrl);
			setCurrentPdfFileName(fileName);
			setPdfViewerOpen(true);
		} else if (isOfficeFile(fileName)) {
			setCurrentOfficeUrl(fileUrl);
			setCurrentOfficeFileName(fileName);
			setOfficeViewerOpen(true);
		} else {
			window.open(fileUrl, '_blank');
		}
	};

	const getFileIcon = (fileName: string) => {
		const extension = fileName.split('.').pop()?.toLowerCase();

		if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
			return <ImageIcon color="primary" />;
		} else if (extension === 'pdf') {
			return <PictureAsPdfIcon color="error" />;
		} else if (['doc', 'docx'].includes(extension || '')) {
			return <DescriptionIcon color="info" />;
		} else if (['xlsx', 'xls'].includes(extension || '')) {
			return <TableViewIcon color="success" />;
		} else if (['mp4'].includes(extension || '')) {
			return <SmartDisplayIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />;
		} else {
			return <InsertDriveFileIcon color="action" />;
		}
	};

	const renderFileIcon = (fileName: string, onClick?: () => void, isCompleted: boolean = true) => {
		const icon = getFileIcon(fileName);
		return (
			<Box
				sx={{
					cursor: onClick && isCompleted ? 'pointer' : 'default',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					opacity: isCompleted ? 1 : 0.6,
				}}
				onClick={onClick && isCompleted ? onClick : undefined}
			>
				{icon}
			</Box>
		);
	};

	const isImageFile = (fileName: string) => {
		const extension = fileName.split('.').pop()?.toLowerCase();
		return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension || '');
	};

	const getImagePreview = (file: File) => {
		return URL.createObjectURL(file);
	};

	const isDuplicateFile = (status: any) => {
		return existingFiles.some(
			(file) => file.filename === status.fileName || file.s3_key.includes(status.key)
		);
	};

	const openLightbox = (imageSrc: string, index: number) => {
		setLightboxSlides([{ src: imageSrc }]);
		setLightboxIndex(0);
		setLightboxOpen(true);
	};

	const openExistingImageLightbox = (imageUrl: string, index: number) => {
		// Check if imageUrl already contains CDN_URL to prevent duplication
		const fullUrl = imageUrl.startsWith(CDN_URL) ? imageUrl : `${CDN_URL}${imageUrl}`;
		setLightboxSlides([{ src: fullUrl }]);
		setLightboxIndex(0);
		setLightboxOpen(true);
	};

	useEffect(() => {
		if (onUploaded && uploadStatus.length > 0) {
			uploadStatus.forEach((status) => {
				if (status.isCompleted && status.key && !processedUploads.has(status.key)) {
					onUploaded(status);
					setProcessedUploads((prev) => {
						const newSet = new Set(prev);
						newSet.add(status.key);
						return newSet;
					});
				}
			});
		}

		// Show success toast when all files are uploaded successfully
		if (uploadStatus.length > 0 && uploadStatus.every((status) => status.isCompleted) && !successToastShown) {
			const completedCount = uploadStatus.filter(status => !isDuplicateFile(status)).length;
			if (completedCount > 0) {
				showSnackbar(
					t('uploadSuccessful') || 'Upload Successful',
					t('allFilesUploadedSuccessfully') || `${completedCount} file(s) uploaded successfully.`,
					'success'
				);
				setSuccessToastShown(true);
			}
		}
	}, [uploadStatus, onUploaded, processedUploads, showSnackbar, t]);

	return (
		<Box sx={{ maxWidth: 800, mx: 'auto', p: 2, width: '100%' }}>
			<Box sx={{ mb: 2, textAlign: 'center' }}>
				<input
					id="file-input"
					type="file"
					multiple={multiple}
					onChange={handleFileChange}
					style={{ display: 'none' }}
				/>
				<Button
					variant="contained"
					color="primary"
					startIcon={<CloudUploadIcon />}
					onClick={() => document.getElementById('file-input')?.click()}
					sx={{
						px: 3,
						py: 1.5,
						textTransform: 'uppercase',
						bgcolor: '#038877',
						'&:hover': { opacity: 0.9 },
						borderRadius: '4px',
						boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
					}}
					size="medium"
				>
					{t('uploadFiles')}
				</Button>
				{showSupportedFormats && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						{t('supportedFormats')}: {allowedFormats.join(', ')} (Max{' '}
						{Math.round(maxFileSize / (1024 * 1024))}MB)
					</Typography>
				)}
			</Box>

			{globalLoading && (
				<Alert severity="info" sx={{ mb: 2, width: '100% !important' }}>
					{t('uploadingFiles')}
				</Alert>
			)}

			{(existingFiles.length > 0 || uploadStatus.length > 0) && (
				<>
					<Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem' }}>
						{t('files')} (
						{existingFiles.length +
							uploadStatus.filter((status) => !isDuplicateFile(status) && status.isCompleted)
								.length}
						)
					</Typography>
					<List sx={{ p: 0 }}>
						{existingFiles.map((file, index) => (
							<ListItem
								key={`existing-${file.id}`}
								divider
								sx={{ py: 1 }}
								secondaryAction={
									showRemoveButton && (
										<IconButton
											edge="end"
											onClick={() => handleExistingFileRemove(file.id)}
											aria-label="Remove file"
											color="error"
											size="small"
										>
											<DeleteIcon />
										</IconButton>
									)
								}
							>
								<ListItemIcon sx={{ minWidth: 40 }}>
									<Box sx={{ position: 'relative' }}>
										{isImageFile(file.filename) ? (
											<Avatar
												src={
													file.s3_key.startsWith(CDN_URL) ? file.s3_key : `${CDN_URL}${file.s3_key}`
												}
												variant="rounded"
												sx={{
													width: 40,
													height: 40,
													cursor: 'pointer',
												}}
												onClick={() => openExistingImageLightbox(file.s3_key, index)}
											>
												<ImageIcon />
											</Avatar>
										) : (
											renderFileIcon(
												file.filename,
												() => handleFileClick(file.s3_key, file.filename, file.fileType),
												true
											)
										)}
										<CheckCircleIcon
											color="success"
											sx={{
												position: 'absolute',
												bottom: -5,
												right: -5,
												fontSize: 16,
												backgroundColor: 'white',
												borderRadius: '50%',
											}}
										/>
									</Box>
								</ListItemIcon>
								<ListItemText
									sx={{ marginLeft: 1 }}
									primary={
										<Typography
											variant="body2"
											sx={{
												fontWeight: 500,
												cursor: !isImageFile(file.filename) ? 'pointer' : 'default',
											}}
											onClick={
												!isImageFile(file.filename)
													? () => handleFileClick(file.s3_key, file.filename, file.fileType)
													: undefined
											}
										>
											{truncate(file.filename, 30)}
										</Typography>
									}
									secondary={
										<Typography
											variant="caption"
											sx={{ display: 'block', color: 'text.secondary' }}
										>
											{t('uploaded')}
										</Typography>
									}
								/>
							</ListItem>
						))}

						{uploadStatus
							.filter((status) => !status.isCompleted || !isDuplicateFile(status))
							.map((status, index) => {
								const file = selectedFiles.find((f) => f.name === status.fileName);
								const isImage = file && isImageFile(file.name);
								const imageSrc = file && isImage ? getImagePreview(file) : undefined;

								return (
									<ListItem
										key={`new-${status.key || index}`}
										divider
										sx={{ py: 1 }}
										secondaryAction={
											showRemoveButton && (
												<IconButton
													edge="end"
													onClick={() => handleRemoveFile(uploadStatus.indexOf(status))}
													aria-label="Remove file"
													color="error"
													size="small"
												>
													<DeleteIcon />
												</IconButton>
											)
										}
									>
										<ListItemIcon sx={{ minWidth: 40 }}>
											{status.isCompleted ? (
												<Box sx={{ position: 'relative' }}>
													{isImage ? (
														<Avatar
															src={imageSrc}
															variant="rounded"
															sx={{
																width: 40,
																height: 40,
																cursor: 'pointer',
															}}
															onClick={() => imageSrc && openLightbox(imageSrc, index)}
														>
															<ImageIcon />
														</Avatar>
													) : (
														renderFileIcon(
															status.fileName,
															status.isCompleted
																? status.key
																	? () =>
																			handleFileClick(status.key, status.fileName, status.fileType)
																	: undefined
																: undefined,
															status.isCompleted
														)
													)}
													<CheckCircleIcon
														color="success"
														sx={{
															position: 'absolute',
															bottom: -5,
															right: -5,
															fontSize: 16,
															backgroundColor: 'white',
															borderRadius: '50%',
														}}
													/>
												</Box>
											) : status.error ? (
												<Box sx={{ position: 'relative' }}>
													{isImage ? (
														<Avatar
															src={imageSrc}
															variant="rounded"
															sx={{
																width: 40,
																height: 40,
																cursor: imageSrc ? 'pointer' : 'default',
															}}
															onClick={() => imageSrc && openLightbox(imageSrc, index)}
														>
															<ImageIcon />
														</Avatar>
													) : (
														renderFileIcon(
															status.fileName,
															false
																? status.key
																	? () =>
																			handleFileClick(status.key, status.fileName, status.fileType)
																	: undefined
																: undefined,
															false
														)
													)}
													<ErrorIcon
														color="error"
														sx={{
															position: 'absolute',
															bottom: -5,
															right: -5,
															fontSize: 16,
															backgroundColor: 'white',
															borderRadius: '50%',
														}}
													/>
												</Box>
											) : isImage ? (
												<Avatar
													src={imageSrc}
													variant="rounded"
													sx={{
														width: 40,
														height: 40,
														cursor: imageSrc ? 'pointer' : 'default',
													}}
													onClick={() => imageSrc && openLightbox(imageSrc, index)}
												>
													<ImageIcon />
												</Avatar>
											) : (
												renderFileIcon(
													status.fileName,
													false
														? status.key
															? () => handleFileClick(status.key, status.fileName, status.fileType)
															: undefined
														: undefined,
													false
												)
											)}
										</ListItemIcon>
										<ListItemText
											sx={{ marginLeft: 1 }}
											primary={
												<Typography
													variant="body2"
													sx={{
														fontWeight: 500,
														cursor:
															!isImage && status.isCompleted && status.key ? 'pointer' : 'default',
													}}
													onClick={
														!isImage && status.isCompleted && status.key
															? () => handleFileClick(status.key, status.fileName, status.fileType)
															: undefined
													}
												>
													{truncate(status.fileName, 30)}
												</Typography>
											}
											secondary={
												<Box sx={{ width: '100%', mt: 1 }}>
													<LinearProgress
														variant="determinate"
														value={status.progress}
														sx={{
															height: 6,
															borderRadius: 4,
															backgroundColor: '#E0E0E0',
															'& .MuiLinearProgress-bar': {
																backgroundColor: '#038877',
															},
														}}
													/>
													<Typography
														variant="caption"
														sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}
													>
														{status.progress}%
														{status.error && (
															<Alert severity="error" sx={{ mt: 1, width: '100% !important' }}>
																{status.error}
															</Alert>
														)}
													</Typography>
												</Box>
											}
										/>
									</ListItem>
								);
							})}
					</List>
				</>
			)}

			{/* Lightbox component for viewing images */}
			<Lightbox
				open={lightboxOpen}
				close={() => setLightboxOpen(false)}
				slides={lightboxSlides}
				index={lightboxIndex}
			/>

			{/* PDF Viewer component for viewing PDFs */}
			<PdfViewer
				open={pdfViewerOpen}
				onClose={() => setPdfViewerOpen(false)}
				pdfUrl={currentPdfUrl}
				fileName={currentPdfFileName}
			/>

			{/* Office Viewer component for viewing Office documents */}
			<OfficeViewer
				open={officeViewerOpen}
				onClose={() => setOfficeViewerOpen(false)}
				documentUrl={currentOfficeUrl}
				fileName={currentOfficeFileName}
			/>

			{uploadStatus.length > 0 && uploadStatus.every((status) => status.isCompleted) && (
				<Alert
					severity="success"
					sx={{
						mt: 2,
						width: '100% !important',
						bgcolor: '#f1f8e9',
						color: '#388E3C',
						border: 'none',
						borderRadius: '4px',
					}}
					icon={<CheckCircleIcon sx={{ color: '#038877' }} />}
				>
					{t('allFilesUploadedSuccessfully')}
				</Alert>
			)}
		</Box>
	);
}
